<template>
  <div class="progress-bar-poc" :class="[`v-${vPos}`, `h-${hPos}`, { 'no-label': !hasLabel }]">
    <div v-if="showBackButton" class="top-row">
      <div
        class="back-button"
        role="button"
        tabindex="0"
        @click="emit('onBackClick')"
        @keydown="onBackKeydown"
      >
        <PlainImage
          v-if="fields.backIcon?.id"
          :image="fields.backIcon"
          :fade-in="false"
          :alt="hasBackLabel ? '' : undefined"
          class="back-icon"
        />
        <div v-if="hasBackLabel" class="back-label">
          <TipTap v-model="backLabelValue" mode="single" />
        </div>
      </div>
      <div v-if="hasLabel && vPos === 'above'" class="label">
        <TipTap v-model="labelValue" mode="single" />
      </div>
    </div>
    <div v-else-if="hasLabel && vPos === 'above'" class="label">
      <TipTap v-model="labelValue" mode="single" />
    </div>

    <div class="bar-wrapper" @transitionend.passive="onTransitionEnd">
      <!-- Mode 1: Line -->
      <div v-if="mode === 1" class="progress-bar progress-bar--line">
        <div class="progress-track">
          <div class="progress-fill" :style="{ width: `${displayProgress}%` }" />
        </div>
      </div>

      <!-- Mode 2: Segments -->
      <div v-else-if="mode === 2" class="progress-bar progress-bar--segments">
        <div v-for="i in segmentCount" :key="i" class="segment-window">
          <div class="progress-fill" :style="getSegmentWindowStyle(i - 1)" />
        </div>
      </div>

      <!-- Mode 3: Groups -->
      <div v-else class="progress-bar progress-bar--groups">
        <template v-for="i in segmentCount" :key="i">
          <div class="segment-window">
            <div class="progress-fill" :style="getSegmentWindowStyle(i - 1)" />
          </div>
          <div
            v-if="i < segmentCount"
            class="group-circle"
            :class="{ completed: isSegmentComplete(i - 1) }"
          >
            <svg
              v-if="isSegmentComplete(i - 1)"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span v-else>{{ i }}</span>
          </div>
        </template>
      </div>

      <div v-if="hasLabel && vPos === 'over'" class="label overlay">
        <TipTap v-model="labelValue" mode="single" />
      </div>
    </div>

    <div v-if="hasLabel && vPos === 'below'" class="label">
      <TipTap v-model="labelValue" mode="single" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useAttrs, watch } from 'vue'
import TipTap from '@render/lib/TipTap'
import PlainImage from '../../generic/PlainImage.vue'
import { usePipe, PipeDisplayMode } from '@render/composables/usePipe'
import { useComponentProcessors } from '@render/composables/useComponentProcessors'
import { useEditorCommands } from '@render/composables/useEditorCommands'
import { useComponentScopedVariables } from '@render/composables/useComponentScopedVariables'
import { useTranslation } from '@render/composables/useTranslation'
import { useDirection } from '@render/composables/useDirection'
import { getColorStyleData, getSizeStyleData } from '@render/composables/useStyleData'
import type { TextDecorationValue } from '@render/types/fieldTypes'
import type { ProgressBarPOCFields as Model, TextStylePreset } from './definition'

defineOptions({ inheritAttrs: false })

const emit = defineEmits<{
  (e: 'onBackClick'): void
}>()

const fields = defineModel<Model>('fields', {
  required: true,
  default: (): Model => ({
    steps: 100,
    designMode: 1,
    segmentCount: 4,
    labelVerticalPosition: 'below',
    labelHorizontalPosition: 'center',
  }),
})

const pipe = usePipe()
const attrs = useAttrs()

const nodeId = computed<string | null>(() => {
  const id = attrs['data-id']
  return typeof id === 'string' && id.length > 0 ? id : null
})

const mode = computed(() => fields.value.designMode ?? 1)
const segmentCount = computed(() => Math.max(1, fields.value.segmentCount ?? 4))
const steps = computed(() => Math.max(1, Number(fields.value.steps) || 100))

const vPos = computed(() => fields.value.labelVerticalPosition ?? 'below')
const hPos = computed(() => fields.value.labelHorizontalPosition ?? 'center')
const labelValue = useTranslation(() => fields.value.label?.key)
const hasLabel = computed(() => Boolean(fields.value.label?.key))

const showBackButton = computed(() => fields.value.showBackButton === true)
const backLabelValue = useTranslation(() => fields.value.backLabel?.key)
const hasBackLabel = computed(
  () => fields.value.showBackLabel === true && Boolean(fields.value.backLabel?.key),
)

function onBackKeydown(event: KeyboardEvent) {
  if (event.target !== event.currentTarget) return
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  emit('onBackClick')
}

// SSR-safe initial value: 0. Real persisted state is read inside onMounted
// after `await pipe.whenReady()` so the deferred-merge in usePipe.ts has had
// a chance to flush localStorage into pipe.variables. Until then, both
// server and client first render show 0 → no hydration mismatch.
const displayProgress = ref(0)

// Storage keys are based on the component's nodeId. Inside a SharedNode
// template the same nodeId is reused across every page that includes that
// SharedNodeRef, so this gives "single bar identity across the pipe" for free.
function targetKey(): string | null {
  return nodeId.value ? `var.global.progressBarPOC.${nodeId.value}.target` : null
}
function displayedKey(): string | null {
  return nodeId.value ? `var.global.progressBarPOC.${nodeId.value}.displayed` : null
}

function persistDisplayed(value: number) {
  const key = displayedKey()
  if (key) pipe.setData(key, value)
}

function clamp(value: number): number {
  const stepIndex = Math.min(steps.value, Math.max(0, Math.round(value)))
  return (stepIndex / steps.value) * 100
}

// Component-scoped vars consumed by descendant TipTap interpolations
// (`comp.current`, `comp.max`, `comp.percent`). `current` is the step index
// 0..steps; `max` is `steps`.
useComponentScopedVariables('ProgressBarPOC', () => {
  const percent = displayProgress.value
  const current = Math.round((percent / 100) * steps.value)
  return { current, max: steps.value, percent: Math.round(percent) }
})

// External output variables — the component WRITES to these as its current
// and max change, but never reads from them. Authors can wire other
// components (text, conditionals, etc.) to read these globally.
function writeOutputVars() {
  const percent = displayProgress.value
  const currentKey = fields.value.currentStepVariable?.key
  const totalKey = fields.value.totalStepsVariable?.key
  if (currentKey) {
    const current = Math.round((percent / 100) * steps.value)
    pipe.setData(`var.${currentKey}`, current)
  }
  if (totalKey) {
    pipe.setData(`var.${totalKey}`, steps.value)
  }
}

watch(displayProgress, () => writeOutputVars())
watch(steps, () => writeOutputVars())
watch(
  () => [fields.value.currentStepVariable?.key, fields.value.totalStepsVariable?.key],
  () => writeOutputVars(),
)

useComponentProcessors({
  setProgress: ({ config }) => {
    const raw = Number(config?.value)
    if (!Number.isFinite(raw)) return
    const targetPercent = clamp(raw)
    const tKey = targetKey()
    if (tKey) pipe.setData(tKey, targetPercent)
    // displayProgress is driven by the onMounted target watcher — mutating
    // it here would race with the snap-to-previousDisplayed step when
    // setProgress fires from an onPageShow handler during onMounted's await.
  },
})

// Editor-only preview: the tailored ProgressBarPOC `setProgress` processor
// editor pushes the form's value live via `builder:editor-command` →
// IframePortal → `EXTERNAL_EDITOR_COMMAND`. We do NOT write to the persisted
// `target` var here — preview is purely visual, we don't want preview ticks
// leaking into runtime persistence the next time the pipe is exported.
useEditorCommands({
  previewSetProgress: ({ config }) => {
    const raw = Number(config?.value)
    if (!Number.isFinite(raw)) return
    displayProgress.value = clamp(raw)
  },
})

// Persist the displayed value once each animation completes — this is what
// allows a later remount (after a no-layout page) to start from where we
// left off and animate to the new target instead of snapping from 0.
function onTransitionEnd(event: TransitionEvent) {
  if (event.propertyName !== 'width') return
  persistDisplayed(displayProgress.value)
}

onMounted(async () => {
  if (pipe.currentMode.value === PipeDisplayMode.EDITOR) {
    // In the editor we don't restore persisted state (avoids confusing
    // authors with stale preview values); the bar starts at 0 and animates
    // as processors fire from the preview iframe.
    return
  }
  await pipe.whenReady()

  const tKey = targetKey()
  const dKey = displayedKey()
  const previousDisplayed = dKey ? Number(pipe.getData(dKey)) || 0 : 0

  // Snap to the previous displayed value (no transition) so the upcoming
  // target change animates from previousDisplayed → target rather than 0.
  displayProgress.value = previousDisplayed
  await nextTick()

  // Reactively bind displayProgress to the persisted target. Reading inside
  // the watcher (not via a captured `target` const) closes the race window
  // where a setProgress processor firing during the await above would write
  // pipe.setData but get stomped by a requestAnimationFrame closing over the
  // pre-write target value. `immediate: true` handles the initial mount;
  // later setData calls (from setProgress or anywhere else) re-fire and
  // schedule a fresh RAF.
  watch(
    () => (tKey ? Number(pipe.getData(tKey)) || 0 : 0),
    (newTarget) => {
      requestAnimationFrame(() => {
        displayProgress.value = newTarget
      })
    },
    { immediate: true },
  )
})

function getSegmentWindowStyle(segmentIndex: number): Record<string, string> {
  return {
    width: `${displayProgress.value * segmentCount.value}%`,
    marginInlineStart: `${-segmentIndex * 100}%`,
  }
}

function isSegmentComplete(segmentIndex: number): boolean {
  const segmentEnd = ((segmentIndex + 1) * 100) / segmentCount.value
  return displayProgress.value >= segmentEnd
}

const overrideBarHeight = computed(() => getSizeStyleData(fields.value.barHeight, 'var(--space-8)'))
const overrideBarColor = computed(() =>
  getColorStyleData(fields.value.barColor, 'var(--color-primary-200)'),
)
const overrideBarFillColor = computed(() =>
  getColorStyleData(fields.value.barFillColor, 'var(--color-primary-500)'),
)
const overrideBarBorderRadius = computed(() =>
  getSizeStyleData(fields.value.barBorderRadius, 'var(--space-4)'),
)
const overrideBarGap = computed(() => getSizeStyleData(fields.value.barGap, 'var(--space-4)'))
const overrideCircleSize = computed(() =>
  getSizeStyleData(fields.value.circleSize, 'var(--space-24)'),
)
const overrideCircleTextColor = computed(() =>
  getColorStyleData(fields.value.circleTextColor, 'var(--color-text-secondary)'),
)
const overrideCircleCheckColor = computed(() =>
  getColorStyleData(fields.value.circleCheckColor, 'var(--color-background-subtle-accent)'),
)

type TextStyleProperty = 'family' | 'weight' | 'size' | 'line-height' | 'color'
function presetVar(preset: TextStylePreset, property: TextStyleProperty): string | null {
  return preset === 'custom' ? null : `var(--${preset}-${property})`
}
function fontStyle(d: TextDecorationValue[] | undefined): string {
  return d?.includes('italic') ? 'italic' : 'normal'
}
function textDecoration(d: TextDecorationValue[] | undefined): string {
  const deco = (d ?? []).filter((v) => v === 'underline' || v === 'line-through')
  return deco.length ? deco.join(' ') : 'none'
}

const labelPreset = computed<TextStylePreset>(() => fields.value.labelTextStyle ?? 'text-label-md')
const labelPresetColor = computed(() =>
  labelPreset.value === 'text-label-md'
    ? 'var(--color-text-primary)'
    : presetVar(labelPreset.value, 'color'),
)
const backLabelPreset = computed<TextStylePreset>(
  () => fields.value.backLabelTextStyle ?? 'text-label-md',
)

const { isRtl } = useDirection()

const css = computed(() => ({
  labelPadding: getSizeStyleData(fields.value.labelPadding, 'var(--space-4)'),
  labelColor:
    getColorStyleData(fields.value.labelColor) ??
    (vPos.value === 'over' ? 'var(--color-text-inverse)' : labelPresetColor.value),
  labelFontFamily: fields.value.labelFontFamily?.trim() || presetVar(labelPreset.value, 'family'),
  labelFontSize:
    getSizeStyleData(fields.value.labelFontSize) ?? presetVar(labelPreset.value, 'size'),
  labelLineHeight:
    getSizeStyleData(fields.value.labelLineHeight) ?? presetVar(labelPreset.value, 'line-height'),
  labelFontWeight: fields.value.labelFontWeight || presetVar(labelPreset.value, 'weight'),
  labelFontStyle: fontStyle(fields.value.labelTextDecoration),
  labelTextDecoration: textDecoration(fields.value.labelTextDecoration),

  backButtonGap: getSizeStyleData(fields.value.backButtonGap, 'var(--space-12)'),
  backIconLabelGap: getSizeStyleData(fields.value.backIconLabelGap, 'var(--space-4)'),
  backIconSize: getSizeStyleData(fields.value.backIconSize, 'var(--space-24)'),
  backIconMirror: isRtl.value ? 'scaleX(-1)' : 'none',
  backLabelColor:
    getColorStyleData(fields.value.backLabelColor) ?? presetVar(backLabelPreset.value, 'color'),
  backLabelFontFamily:
    fields.value.backLabelFontFamily?.trim() || presetVar(backLabelPreset.value, 'family'),
  backLabelFontSize:
    getSizeStyleData(fields.value.backLabelFontSize) ?? presetVar(backLabelPreset.value, 'size'),
  backLabelLineHeight:
    getSizeStyleData(fields.value.backLabelLineHeight) ??
    presetVar(backLabelPreset.value, 'line-height'),
  backLabelFontWeight:
    fields.value.backLabelFontWeight || presetVar(backLabelPreset.value, 'weight'),
  backLabelFontStyle: fontStyle(fields.value.backLabelTextDecoration),
  backLabelTextDecoration: textDecoration(fields.value.backLabelTextDecoration),
}))
</script>

<style scoped lang="scss">
.progress-bar-poc {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: v-bind('css.labelPadding');

  /* Author "left"/"right" are treated as reading-relative start/end, so the
     label switches sides under RTL. (The `over` overlay variant already mirrors
     via flex-start/flex-end below.) */
  &.h-left .label {
    text-align: start;
  }
  &.h-center .label {
    text-align: center;
  }
  &.h-right .label {
    text-align: end;
  }
}

.bar-wrapper {
  position: relative;
  width: 100%;
}

.top-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  column-gap: v-bind('css.backButtonGap');

  .progress-bar-poc.h-center & {
    grid-template-columns: 1fr auto 1fr;
  }

  > .back-button {
    grid-area: 1 / 1;
    justify-self: start;
  }

  > .label {
    grid-area: 1 / 2;
  }
}

.back-button {
  display: flex;
  align-items: center;
  gap: v-bind('css.backIconLabelGap');
  cursor: pointer;
}

.back-icon {
  display: block;
  flex-shrink: 0;
  width: v-bind('css.backIconSize');
  height: v-bind('css.backIconSize');
  object-fit: contain;
  transform: v-bind('css.backIconMirror');
}

.back-label {
  font-family: v-bind('css.backLabelFontFamily');
  font-size: v-bind('css.backLabelFontSize');
  line-height: v-bind('css.backLabelLineHeight');
  font-weight: v-bind('css.backLabelFontWeight');
  font-style: v-bind('css.backLabelFontStyle');
  text-decoration: v-bind('css.backLabelTextDecoration');
  color: v-bind('css.backLabelColor');
  white-space: nowrap;
}

.label {
  font-family: v-bind('css.labelFontFamily');
  font-size: v-bind('css.labelFontSize');
  line-height: v-bind('css.labelLineHeight');
  font-weight: v-bind('css.labelFontWeight');
  font-style: v-bind('css.labelFontStyle');
  text-decoration: v-bind('css.labelTextDecoration');
  color: v-bind('css.labelColor');

  &.overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    padding: 0 var(--space-8);
    pointer-events: none;
    z-index: 2;

    .progress-bar-poc.h-left & {
      justify-content: flex-start;
    }
    .progress-bar-poc.h-center & {
      justify-content: center;
    }
    .progress-bar-poc.h-right & {
      justify-content: flex-end;
    }
  }
}

.progress-bar {
  display: flex;
  align-items: center;
  width: 100%;
}

.progress-track {
  flex: 1;
  height: v-bind(overrideBarHeight);
  background: v-bind(overrideBarColor);
  border-radius: v-bind(overrideBarBorderRadius);
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: v-bind(overrideBarFillColor);
  border-radius: inherit;
  transition: width 0.5s ease-in-out;
  will-change: width;
}

.segment-window {
  flex: 1;
  height: v-bind(overrideBarHeight);
  background: v-bind(overrideBarColor);
  border-radius: v-bind(overrideBarBorderRadius);
  overflow: hidden;
}

.progress-bar--segments {
  gap: v-bind(overrideBarGap);
}

.progress-bar--groups {
  gap: v-bind(overrideBarGap);
}

.group-circle {
  flex-shrink: 0;
  width: v-bind(overrideCircleSize);
  height: v-bind(overrideCircleSize);
  border-radius: 50%;
  background: v-bind(overrideBarColor);
  color: v-bind(overrideCircleTextColor);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: calc(v-bind(overrideCircleSize) * 0.45);
  font-weight: bold;
  transition:
    background 0.3s ease,
    color 0.3s ease;

  &.completed {
    background: v-bind(overrideBarFillColor);
    color: v-bind(overrideCircleCheckColor);
  }

  svg {
    width: 60%;
    height: 60%;
  }
}
</style>
